// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCheckoutCartId } from "@bg/App/Store/Selectors/Checkout/CheckoutSelectors";
import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@bg/Overrides/Components/AddressInfoDisplay";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { parseBoolean } from "@bg/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import setFulfillmentMethod from "@insite/client-framework/Store/Context/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouse from "@insite/client-framework/Store/Context/Handlers/UpdatePickUpWarehouse";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setFulfillmentMethodForCart from "@insite/client-framework/Store/Data/Carts/Handlers/SetFulfillmentMethod";
import updatePickUpWarehouseForCart from "@insite/client-framework/Store/Data/Carts/Handlers/UpdatePickUpWarehouse";
import translate from "@insite/client-framework/Translate";
import { WarehouseModel } from "@insite/client-framework/Types/ApiModels";
import FindLocationModal, { FindLocationModalStyles } from "@insite/content-library/Components/FindLocationModal";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import Radio, { RadioComponentProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext, { useToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    onModalClose: () => void;
    modalIsOpen: boolean;
    fulfillmentMethod: string;
    quoteID: string;
}

const mapStateToProps = (state: ApplicationState) => {
    const cartId = getCheckoutCartId(state);
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        cart: cartState.value,
        session: state.context.session,
        accountSettings: getSettingsCollection(state).accountSettings,
        updateCartInsteadOfContext: cartState.value?.isAwaitingApproval || !!cartId,
    };
};

const mapDispatchToProps = {
    setFulfillmentMethod,
    updatePickUpWarehouse: makeHandlerChainAwaitable(updatePickUpWarehouse),
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart: makeHandlerChainAwaitable(updatePickUpWarehouseForCart),
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ChangeQuoteFulfillmentMethodSelectorStyles {
    container?: GridContainerProps;
    radioGroupGridItem?: GridItemProps;
    fulfillmentMethodRadioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    fulfillmentMethodRadio?: FieldSetPresentationProps<RadioComponentProps>;
    pickupLocationGridItem?: GridItemProps;
    saveChangeGridItem?: GridItemProps;
    pickUpLocation?: PickUpLocationStyles;
    saveChange?: ButtonPresentationProps;
}
interface PickUpLocationStyles {
    wrapper?: InjectableCss;
    headerText?: TypographyProps;
    changeLink?: LinkPresentationProps;
    warehouseAddress?: WarehouseAddressInfoDisplayStyles;
    findLocationModal?: FindLocationModalStyles;
}
interface WarehouseAddressInfoDisplayStyles {
    wrapper?: InjectableCss;
    nameText?: TypographyProps;
    address?: AddressInfoDisplayStyles;
}

export const changeQuoteFulfillmentMethodStyles: ChangeQuoteFulfillmentMethodSelectorStyles = {
    radioGroupGridItem: {
        width: 12,
    },
    pickupLocationGridItem: {
        width: 12,
    },
    saveChangeGridItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    pickUpLocation: {
        wrapper: {
            css: css`
                margin-top: 0;
                padding-top: 0;
            `,
        },
        headerText: {
            variant: "h5",
        },
        changeLink: {
            css: css`
                margin-left: 15px;
            `,
        },
        warehouseAddress: {
            wrapper: {
                css: css`
                    margin-top: 8px;
                `,
            },
            nameText: {
                as: "p",
                lineHeight: "19px",
                weight: fontConfig.weight.bold,
                css: css`
                    margin-top: 20px;
                `,
            },
        },
    },
    saveChange: {
        css: css`
            &&&&&& {
                background: ${getColor("primary.main")};
                padding: 0 20px;
                color: ${getColor("common.white")};
                &:hover,
                &:focus {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("primary.main")};
                    color: ${getColor("primary.main")};
                }
            }
        `,
    },
};
const styles = changeQuoteFulfillmentMethodStyles;
const ChangeQuoteFulfillmentMethodSelector: FC<Props> = ({
    cart,
    session,
    accountSettings,
    setFulfillmentMethod,
    updatePickUpWarehouse,
    onModalClose,
    modalIsOpen,
    fulfillmentMethod,
    quoteID,
    updateCartInsteadOfContext,
    setFulfillmentMethodForCart,
    updatePickUpWarehouseForCart,
}) => {
    const [radioValue, setRadioValue] = useState(fulfillmentMethod);
    const toasterContext = useToaster();

    if (!accountSettings || !accountSettings.enableWarehousePickup) {
        return null;
    }
    const fulfillmentMethodChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        setRadioValue(event.currentTarget.value);
    };
    const fulfillmentMethodSave = () => {
        if (updateCartInsteadOfContext && cart) {
            setFulfillmentMethodForCart({
                cart,
                fulfillmentMethod: radioValue as "Ship" | "PickUp",
            } as any);
        } else {
            setFulfillmentMethod({
                fulfillmentMethod: radioValue as "Ship" | "PickUp",
                // XNG-Changes
                properties: { quoteId: quoteID },
            } as any);
        }

        onModalClose();
    };

    const handlePickUpAddressChange = async (pickUpWarehouse: WarehouseModel) => {
        if (updateCartInsteadOfContext && cart) {
            await updatePickUpWarehouseForCart({ cart, pickUpWarehouse });
        } else {
            await updatePickUpWarehouse({ pickUpWarehouse });
        }

        toasterContext.addToast({
            body: translate("Pick Up Address Updated"),
            messageType: "success",
        });
    };
    const modalCloseHandler = () => {
        onModalClose();
    };

    return (
        <Modal headline="Change Fulfillment Method" isOpen={modalIsOpen} handleClose={modalCloseHandler}>
            <GridContainer {...styles.container}>
                <GridItem {...styles.radioGroupGridItem}>
                    <RadioGroup
                        label={translate("Fulfillment Method")}
                        value={radioValue}
                        {...styles.fulfillmentMethodRadioGroup}
                        onChangeHandler={fulfillmentMethodChangeHandler}
                        data-test-selector="changeQuoteFulfillmentMethod"
                    >
                        <Radio
                            value={FulfillmentMethod.Ship}
                            {...styles.fulfillmentMethodRadio}
                            data-test-selector="fulfillmentMethod_ship"
                        >
                            {translate("Ship")}
                        </Radio>
                        <Radio
                            value={FulfillmentMethod.PickUp}
                            {...styles.fulfillmentMethodRadio}
                            data-test-selector="fulfillmentMethod_pickUp"
                        >
                            {translate("Pick Up")}
                        </Radio>
                    </RadioGroup>
                </GridItem>
                <GridItem {...styles.pickupLocationGridItem}>
                    {radioValue === FulfillmentMethod.PickUp && session.pickUpWarehouse && (
                        <PickUpLocation warehouse={session.pickUpWarehouse} onChange={handlePickUpAddressChange} />
                    )}
                </GridItem>
                <GridItem {...styles.saveChangeGridItem}>
                    <Button {...styles.saveChange} onClick={fulfillmentMethodSave}>
                        {translate("Save Change")}
                    </Button>
                </GridItem>
            </GridContainer>
        </Modal>
    );
};
interface PickUpLocationProps {
    warehouse: WarehouseModel;
    onChange: (address: WarehouseModel) => void;
}
const PickUpLocation: FC<PickUpLocationProps> = ({ warehouse, onChange }) => {
    const componentStyles = styles.pickUpLocation || {};
    const [isFindLocationOpen, setIsFindLocationOpen] = React.useState(false);
    // XNG-Changes
    const disablePickupLocationChange = useAppSelector(state =>
        parseBoolean((getSettingsCollection(state) as any)?.customSettings.disablePickupLocationChange),
    );

    const handleOpenFindLocation = () => {
        setIsFindLocationOpen(true);
    };
    const handleFindLocationModalClose = () => setIsFindLocationOpen(false);
    const handleWarehouseSelected = (warehouse: WarehouseModel) => {
        onChange(warehouse);
        setIsFindLocationOpen(false);
    };
    return (
        <>
            <StyledWrapper {...componentStyles.wrapper} data-test-selector="changeQuote_pickUpLocation">
                <Typography {...componentStyles.headerText}>{translate("Pick Up Address")}</Typography>
                {/* XNG-Changes: BG-3037 -- remove change ability when disablePickupLocationChange */}
                {!disablePickupLocationChange && (
                    <Link
                        onClick={handleOpenFindLocation}
                        {...componentStyles.changeLink}
                        data-test-selector="changeCustomer_pickUpLocation_findLocation"
                    >
                        {translate("Change")}
                    </Link>
                )}
                <WarehouseAddressInfoDisplay warehouse={warehouse} />
            </StyledWrapper>
            <FindLocationModal
                modalIsOpen={isFindLocationOpen}
                onWarehouseSelected={handleWarehouseSelected}
                onModalClose={handleFindLocationModalClose}
                forCart
                extendedStyles={componentStyles.findLocationModal}
            />
        </>
    );
};
interface WarehouseAddressInfoDisplayProps {
    warehouse: WarehouseModel;
}
const WarehouseAddressInfoDisplay: FC<WarehouseAddressInfoDisplayProps> = ({ warehouse }) => {
    const componentStyles =
        styles.pickUpLocation && styles.pickUpLocation.warehouseAddress ? styles.pickUpLocation.warehouseAddress : {};
    return (
        <StyledWrapper {...componentStyles.wrapper}>
            <Typography {...componentStyles.nameText} data-test-selector="changeQuote_warehouseName">
                {warehouse.description || warehouse.name}
            </Typography>
            <AddressInfoDisplay
                address1={warehouse.address1}
                address2={warehouse.address2}
                city={warehouse.city}
                countryId={warehouse.countryId || ""}
                state={warehouse.state}
                postalCode={warehouse.postalCode}
                phone={warehouse.phone}
                extendedStyles={componentStyles.address}
            />
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeQuoteFulfillmentMethodSelector);
